import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import {
  getDealThesisInputCaseForecastDate,
  getDealThesisInputCaseHistoricForecastDate,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { useLocale } from '@app/core/hooks/useLocale';
import { getPwermValuationValueDriver } from '@app/shared/models/helpers/project-helpers';
import { DealThesisSpacerRow2 } from '../DealThesisSpacerRow2';
import DealThesisEmptyHistoricDataColumns from '../../deal-thesis-utils/DealThesisEmptyHistoricDataColumns';
import useDealThesisData from '../../deal-thesis-utils/useDealThesisData';

const ExitValueDriverSection2: FC = (): JSX.Element => {
  const { l } = useLocale();
  const { caseId } = useParams();
  const project = useAppSelector((state) => state.project.projectDraft);
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);
  const {
    caseResultsForecastDates,
    pwermCaseDataForecasts,
    pwermInputHistoricForecasts,
    showHistoricDataColumns,
    getMaxColspan,
    historicDataForecastYearsResults,
  } = useDealThesisData(caseId);

  const valueDriverName = getPwermValuationValueDriver(project).narrative;
  const valueDriverMetricId = project.pwermInput.valueDriverActiveMetric;

  return (
    <>
      <tbody>
        <tr>
          <th className="table-primary__cell--header">{l('_ExitValueDriverTitle')}</th>
          <td className="table-primary__cell--header" colSpan={getMaxColspan() - 1} />
        </tr>
        <tr className="table-primary__row--plain">
          <th className="table-primary__cell--vertical-separator">
            <CellValue value={valueDriverName} placeholder={l('_ExitValueDriver')} />
          </th>
          <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
          {showHistoricDataColumns &&
            historicDataForecastYearsResults.map((_, index) => (
              <td key={index + 'exit-driver-historic'}>
                <CellValue
                  {...amountRules}
                  value={pwermInputHistoricForecasts[index].metricValues[valueDriverMetricId]}
                  alignment={Alignment.Right}
                />
              </td>
            ))}
          <td>
            <CellValue
              {...amountRules}
              value={project.pwermInput.valuationDateMetrics[valueDriverMetricId]}
              alignment={Alignment.Right}
            />
          </td>
          {pwermCaseDataForecasts?.map((forecast, index) => (
            <td key={index + 'exit-driver'}>
              <CellValue
                {...amountRules}
                value={forecast.metricValues[valueDriverMetricId]}
                alignment={Alignment.Right}
              />
            </td>
          ))}
          <td />
        </tr>
        <tr>
          <th className="table-primary__cell--vertical-separator">
            {l('_ExitValueDriverYoYGrowth', { exitValueDriver: valueDriverName })}
          </th>
          <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
          {showHistoricDataColumns &&
            historicDataForecastYearsResults.map((historicForecast) => (
              <td key={historicForecast.historicYears}>
                <CellValue
                  {...enteredPercentageViewRules}
                  value={getCalculatedProjectValueInPercent(
                    getDealThesisInputCaseHistoricForecastDate(
                      results,
                      caseId!,
                      historicForecast.historicYears,
                      (historicForecast) => historicForecast.valueDriverYearOnYearGrowth
                    )
                  )}
                />
              </td>
            ))}
          <td />
          {caseResultsForecastDates.map((forecast, index) => (
            <td key={index + 'exit-driver-yoy-growth'}>
              <CellValue
                {...enteredPercentageViewRules}
                value={getCalculatedProjectValueInPercent(
                  getDealThesisInputCaseForecastDate(
                    results,
                    caseId!,
                    forecast.forecastId,
                    (forecast) => forecast.valueDriverYearOnYearGrowth
                  )
                )}
              />
            </td>
          ))}
          <td />
        </tr>
        <tr>
          <th className="table-primary__cell--vertical-separator">
            {l('_ExitValueDriverCAGR', { exitValueDriver: valueDriverName })}
          </th>
          <DealThesisEmptyHistoricDataColumns isHistoricEventsSection={false} />
          {showHistoricDataColumns &&
            historicDataForecastYearsResults.map((historicForecast, index) => (
              <td key={index + 'exit-driver-historic-cagr'}>
                <CellValue
                  {...enteredPercentageViewRules}
                  value={getCalculatedProjectValueInPercent(
                    getDealThesisInputCaseHistoricForecastDate(
                      results,
                      caseId!,
                      historicForecast.historicYears,
                      (historicForecast) => historicForecast.valueDriverCompoundAnnualGrowth
                    )
                  )}
                />
              </td>
            ))}
          <td />
          {caseResultsForecastDates.map((forecast, index) => (
            <td key={index + 'exit-driver-cagr'}>
              <CellValue
                {...enteredPercentageViewRules}
                value={getCalculatedProjectValueInPercent(
                  getDealThesisInputCaseForecastDate(
                    results,
                    caseId!,
                    forecast.forecastId,
                    (forecast) => forecast.valueDriverCompoundAnnualGrowth
                  )
                )}
              />
            </td>
          ))}
          <td />
        </tr>
        <DealThesisSpacerRow2 />
      </tbody>
    </>
  );
};

export default ExitValueDriverSection2;
